import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Shield, LogOut, ArrowLeft, Upload, Search, FolderOpen, Image as ImageIcon,
  Film, Music, FileText, File as FileIcon, Trash2, Download, Tag, X, Edit3
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const KIND_ICONS = {
  image: ImageIcon,
  video: Film,
  audio: Music,
  document: FileText,
  other: FileIcon,
};

const KIND_COLORS = {
  image: 'text-emerald-600 bg-emerald-50',
  video: 'text-purple-600 bg-purple-50',
  audio: 'text-orange-600 bg-orange-50',
  document: 'text-blue-600 bg-blue-50',
  other: 'text-gray-600 bg-gray-50',
};

const kindOf = (contentType = '') => {
  const ct = contentType.toLowerCase();
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('video/')) return 'video';
  if (ct.startsWith('audio/')) return 'audio';
  if (ct.includes('pdf') || ct.includes('document') || ct.startsWith('text/')) return 'document';
  return 'other';
};

const formatBytes = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const MediaGalleryPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [query, setQuery] = useState('');
  const [folderFilter, setFolderFilter] = useState(''); // '' = all, null-string handled server-side
  const [kindFilter, setKindFilter] = useState('');

  const [uploadFolder, setUploadFolder] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ filename: '', folder: '', tags: '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (folderFilter) params.append('folder', folderFilter);
      if (kindFilter) params.append('kind', kindFilter);

      const [itemsRes, foldersRes, statsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/media?${params.toString()}`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/media/folders`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/media/stats`, { withCredentials: true }),
      ]);
      setItems(itemsRes.data);
      setFolders(foldersRes.data.folders || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [query, folderFilter, kindFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const doUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let successCount = 0;
    for (const file of files) {
      try {
        const params = new URLSearchParams();
        if (uploadFolder.trim()) params.append('folder', uploadFolder.trim());
        if (uploadTags.trim()) params.append('tags', uploadTags.trim());
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/media/upload?${params.toString()}`,
          fd,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
        successCount++;
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || 'upload failed'}`);
      }
    }
    setUploading(false);
    if (successCount > 0) toast.success(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
    fetchAll();
  };

  const onFileInputChange = (e) => {
    doUpload(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload(Array.from(e.dataTransfer.files || []));
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      filename: item.filename,
      folder: item.folder || '',
      tags: (item.tags || []).join(', '),
    });
  };

  const saveEdit = async () => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/media/${editItem.id}`,
        {
          filename: editForm.filename,
          folder: editForm.folder,
          tags: editForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
        },
        { withCredentials: true }
      );
      toast.success('Saved');
      setEditItem(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const doDelete = async (item) => {
    if (!window.confirm(`Delete "${item.filename}"?`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/media/${item.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const downloadUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/media/${id}/download`;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="media-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">InvestiGate</span>
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 rounded">MEDIA</span>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/dashboard" className="text-sm text-gray-600 hover:text-gray-900 flex items-center" data-testid="media-back-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Link>
            <span className="text-sm text-gray-600">{user?.email}</span>
            <button onClick={handleLogout} className="text-gray-600 hover:text-gray-900" data-testid="media-logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Media Gallery</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatMini label="Total Files" value={stats.total_files} testid="stat-total" />
            <StatMini label="Storage Used" value={formatBytes(stats.total_bytes)} testid="stat-bytes" />
            <StatMini label="Images" value={stats.by_kind?.image || 0} testid="stat-image" />
            <StatMini label="Videos" value={stats.by_kind?.video || 0} testid="stat-video" />
            <StatMini label="Documents" value={stats.by_kind?.document || 0} testid="stat-doc" />
          </div>
        )}

        {/* Upload Dropzone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`bg-white rounded-lg shadow p-6 mb-6 border-2 border-dashed transition ${
            dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
          }`}
          data-testid="upload-zone"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 text-center md:text-left">
              <Upload className="w-8 h-8 text-blue-600 mx-auto md:mx-0 mb-2" />
              <p className="text-sm font-medium text-gray-900">
                {uploading ? 'Uploading…' : 'Drop files here or click to browse'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Up to 50 MB per file · images, videos, audio, PDFs, documents</p>
            </div>
            <div className="grid grid-cols-2 gap-2 flex-1">
              <input
                type="text"
                placeholder="Folder (optional)"
                value={uploadFolder}
                onChange={(e) => setUploadFolder(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="upload-folder-input"
              />
              <input
                type="text"
                placeholder="Tags: comma,separated"
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="upload-tags-input"
              />
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              data-testid="upload-btn"
            >
              <Upload className="w-4 h-4 mr-1" />
              {uploading ? 'Uploading…' : 'Choose Files'}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              multiple
              onChange={onFileInputChange}
              className="hidden"
              data-testid="file-input"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 flex items-center space-x-2">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search filename or tag..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchAll()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="media-search-input"
              />
            </div>
            <select
              value={folderFilter}
              onChange={(e) => setFolderFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
              data-testid="folder-filter"
            >
              <option value="">All folders</option>
              {folders.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
              data-testid="kind-filter"
            >
              <option value="">All types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="audio">Audio</option>
              <option value="document">Documents</option>
              <option value="other">Other</option>
            </select>
            <button onClick={fetchAll} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-testid="media-search-btn">
              Refresh
            </button>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-lg shadow text-center py-16" data-testid="media-empty">
            <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No files yet</h3>
            <p className="text-sm text-gray-500">Drop files above or click "Choose Files" to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {items.map((item) => {
              const kind = kindOf(item.content_type);
              const Icon = KIND_ICONS[kind];
              const isImage = kind === 'image';
              return (
                <div key={item.id} className="bg-white rounded-lg shadow overflow-hidden group" data-testid={`media-card-${item.id}`}>
                  <div className={`aspect-square flex items-center justify-center ${KIND_COLORS[kind]} relative`}>
                    {isImage ? (
                      <img
                        src={downloadUrl(item.id)}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Icon className="w-14 h-14 opacity-70" />
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-xs font-medium text-gray-900 truncate" title={item.filename}>{item.filename}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5 flex items-center justify-between">
                      <span>{formatBytes(item.size)}</span>
                      {item.folder && <span className="truncate ml-2" title={item.folder}>📁 {item.folder}</span>}
                    </div>
                    {item.tags && item.tags.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {item.tags.slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">#{t}</span>
                        ))}
                        {item.tags.length > 3 && <span className="text-[10px] text-gray-400">+{item.tags.length - 3}</span>}
                      </div>
                    )}
                    <div className="mt-2 flex gap-1">
                      <a
                        href={downloadUrl(item.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 flex items-center justify-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                        data-testid={`media-download-${item.id}`}
                      >
                        <Download className="w-3 h-3 mr-1" /> Open
                      </a>
                      <button
                        onClick={() => openEdit(item)}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        data-testid={`media-edit-${item.id}`}
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => doDelete(item)}
                        className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                        data-testid={`media-delete-${item.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="edit-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Edit media</h2>
              <button onClick={() => setEditItem(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filename</label>
                <input
                  type="text"
                  value={editForm.filename}
                  onChange={(e) => setEditForm({ ...editForm, filename: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="edit-filename-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Folder</label>
                <input
                  type="text"
                  value={editForm.folder}
                  onChange={(e) => setEditForm({ ...editForm, folder: e.target.value })}
                  placeholder="(root)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="edit-folder-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  <Tag className="w-3 h-3 mr-1" /> Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="edit-tags-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={saveEdit}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  data-testid="edit-save-btn"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditItem(null)}
                  className="flex-1 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  data-testid="edit-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatMini = ({ label, value, testid }) => (
  <div className="bg-white p-3 rounded-lg shadow" data-testid={testid}>
    <p className="text-xs text-gray-600">{label}</p>
    <p className="text-lg font-bold text-gray-900">{value}</p>
  </div>
);

export default MediaGalleryPage;
